// ============================================================
// conditionPicker - the Add-Condition picker's decisions, as data
// ============================================================
// Pure answers for AddConditionModal and its parts: what a badge click
// does, what the number picker is called and offers for each kind of
// suffix, what the Hallucinating hold caption says, and what a custom
// modifier row emits. No React, so each is tested on its own.

import { buildCondCode, buildCustomPen } from '../engine/conditions';
import { buildHallucinationCode } from '../engine/hallucination';
import type { StackPolicy } from '../engine/stackPolicy';
import type { ConditionCategory, EligibleCondition } from '../engine/conditionEligibility';

/** What pressing a condition badge does. */
export type BadgeAction = 'blocked' | 'duration' | 'location' | 'add';

interface BadgeContext {
  /** The GM unlocked this immunity with the lock icon. */
  overridden: boolean;
  /** Shift held: a one-off override of the immunity. */
  shiftKey: boolean;
  /** No durations offered (a condition the ground imposes). */
  timeless: boolean;
  /** The body has locations to pick from (amorphous and hordes have none). */
  hasLocations: boolean;
}

/**
 * The action a badge press takes. `timeless` suppresses DURATIONS, never
 * LEVELS: Fatigue's suffix is a magnitude, not a countdown, and ground
 * that leaves you Fatigued 2 is saying how tiring it is, not for how
 * long (v7.78.0). A location condition on a body with no locations adds
 * its bare code.
 */
export function badgeAction(cond: EligibleCondition, ctx: BadgeContext): BadgeAction {
  if (cond.immune && !ctx.overridden && !ctx.shiftKey) return 'blocked';
  if (cond.hasDuration && (cond.durationUnit === 'level' || !ctx.timeless)) return 'duration';
  if (cond.requiresLocation && ctx.hasLocations) return 'location';
  return 'add';
}

/** The eligible conditions grouped by category, every category present. */
export function groupByCategory(eligible: EligibleCondition[]): Record<ConditionCategory, EligibleCondition[]> {
  const grouped: Record<ConditionCategory, EligibleCondition[]> = {
    fatal: [],
    debilitating: [],
    status: [],
    location: [],
    vehicle: [],
  };
  for (const cond of eligible) grouped[cond.category].push(cond);
  return grouped;
}

/** How the number picker presents one kind of suffix. */
export interface NumberPickerCopy {
  /** The picker group's accessible name. */
  groupLabel: string;
  /** The caption above the presets. */
  caption: string;
  presets: readonly number[];
  /** A round countdown also offers "no countdown at all". */
  offersPermanent: boolean;
  /** Hallucinating's intensity can be rolled and held. */
  isIntensity: boolean;
  /** The custom input's accessible name. */
  customLabel: string;
}

const ROUNDS: NumberPickerCopy = {
  groupLabel: 'Duration picker',
  caption: 'Duration (rounds)',
  presets: [1, 2, 3, 5, 10],
  offersPermanent: true,
  isIntensity: false,
  customLabel: 'Custom duration',
};

// Fatigue and any future level-based condition: the suffix is a magnitude,
// not a countdown - label it "Level" and no Permanent. Haywire's number is
// a SEVERITY row that decays one row per round on its own (v8.199.8).
// Hallucinating (v8.357.0): 1 faint … 10 lost - a figure or a ROLL.
const NUMBER_PICKER_COPY: Record<string, NumberPickerCopy> = {
  level: {
    ...ROUNDS,
    groupLabel: 'Level picker',
    caption: 'Level',
    offersPermanent: false,
    customLabel: 'Custom level',
  },
  severity: {
    ...ROUNDS,
    groupLabel: 'Severity picker',
    caption: 'Severity (1 Minor → 4 Total Shutdown)',
    presets: [1, 2, 3, 4],
    offersPermanent: false,
  },
  intensity: {
    ...ROUNDS,
    groupLabel: 'Intensity picker',
    caption: 'Intensity (1 faint → 10 lost)',
    presets: [1, 3, 5, 7, 9, 10],
    offersPermanent: false,
    isIntensity: true,
  },
};

/** The number picker's copy for a condition's suffix unit (a round countdown by default). */
export function numberPickerCopy(unit: EligibleCondition['durationUnit']): NumberPickerCopy {
  return NUMBER_PICKER_COPY[unit ?? 'round'] ?? ROUNDS;
}

/**
 * The code for the number picked: Hallucinating's intensity (stepping, or
 * held for `holdRounds` - '' steps, 0 holds until removed) or a plain suffix.
 */
export function numberedCode(cond: EligibleCondition, n: number, holdRounds: string): string {
  if (cond.durationUnit !== 'intensity') return buildCondCode(cond.code, n);
  const rounds = holdRounds === '' ? null : Math.max(0, parseInt(holdRounds, 10) || 0);
  return buildHallucinationCode({ intensity: n, rounds });
}

/** The Hallucinating hold caption: '' steps down, N > 0 holds N rounds, 0 holds until removed. */
export function holdCaption(holdRounds: string): string {
  if (holdRounds === '') return 'Steps down a rung a round - or hold it for';
  return parseInt(holdRounds, 10) > 0 ? 'Held at full intensity for' : 'Held until removed -';
}

/** The custom modifier row as typed. */
export interface CustomModifierInput {
  scope: string;
  value: string;
  rounds: string;
  policy: StackPolicy | 'session';
  cap: string;
  timeless: boolean;
}

/** What a custom modifier row emits. */
export interface CustomModifierEntry {
  code: string;
  /** The picker's own stacking ruling; absent = the session's. */
  stacking?: StackPolicy;
  /** The answer to remember for the next picker (null = the table's); absent = remember nothing. */
  remember?: StackPolicy | null;
}

/**
 * The code a custom modifier row adds, or null for a zero or empty value.
 * A cap only means something under raise, and a timeless modifier has no
 * rounds, no cap and no policy of its own.
 */
export function customModifierEntry(input: CustomModifierInput): CustomModifierEntry | null {
  const value = parseInt(input.value, 10);
  if (!Number.isFinite(value) || value === 0) return null;
  if (input.timeless) return { code: buildCustomPen({ scopes: [input.scope], value, rounds: 0 }) };
  const rounds = parseInt(input.rounds, 10);
  const code = buildCustomPen({ scopes: [input.scope], value, rounds: rounds > 0 ? rounds : 0, ...capField(input) });
  if (input.policy === 'session') return { code, remember: null };
  return { code, stacking: input.policy, remember: input.policy };
}

/** The cap a raise carries: none under any other policy, and none for a blank or zero. */
function capField(input: CustomModifierInput): { cap?: number } {
  if (input.policy !== 'raise') return {};
  const cap = parseInt(input.cap, 10);
  return Number.isFinite(cap) && cap !== 0 ? { cap } : {};
}

/** A copy of `set` with `code` added, or removed if it was there. */
export function toggledIn(set: Set<string>, code: string): Set<string> {
  const next = new Set(set);
  if (next.has(code)) next.delete(code);
  else next.add(code);
  return next;
}

/** The code a toggle leaves open: none if `code` was the one open, else `code`. */
export function toggledCode(open: string | null, code: string): string | null {
  return open === code ? null : code;
}
